import { ChangeEvent, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  FormControl,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  IconButton,
  Typography,
  Chip,
  Grid2 as Grid,
  Divider,
  ButtonGroup
} from "@mui/material";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import FingerprintRoundedIcon from "@mui/icons-material/FingerprintRounded";

import {
  useAccountDates,
  useAccounts,
  useEmails,
  AccountSummary,
  DateInfo,
  EmailInfo,
  useEmailPreview,
  buildAttachmentDownloadUrl
} from "../api/hooks";
import {
  buildArchiveDownloadUrl,
  buildDigestDownloadUrl,
  buildEmailDownloadUrl
} from "../utils/links";
import { apiClient, extractApiError, API_BASE_URL } from "../api/client";
import { useNotificationStore } from "../store/notificationStore";

function formatBytes(bytes: number) {
  if (!Number.isFinite(bytes) || bytes <= 0) return "-";
  const units = ["B", "KB", "MB", "GB"];
  let value = bytes;
  let index = 0;
  while (value >= 1024 && index < units.length - 1) {
    value /= 1024;
    index += 1;
  }
  const digits = value >= 10 || index === 0 ? 0 : 1;
  return `${value.toFixed(digits)} ${units[index]}`;
}

function formatDigest(hash?: string | null) {
  if (!hash) return "-";
  return hash.length > 14 ? `${hash.slice(0, 12)}...` : hash;
}

function resolveStatusColor(status?: string | null) {
  switch (status?.toLowerCase()) {
    case "completed":
    case "success":
      return "success";
    case "running":
    case "in_progress":
      return "info";
    case "failed":
    case "error":
      return "error";
    default:
      return "default";
  }
}

function getDefaultAccount(accounts?: AccountSummary[]): string | undefined {
  if (!accounts || accounts.length === 0) return undefined;
  return accounts[0]?.name;
}

function getDefaultYear(account?: AccountSummary, current?: string): string | undefined {
  if (!account || account.years.length === 0) return undefined;
  if (current && account.years.includes(current)) {
    return current;
  }
  return account.years[0];
}

function getDefaultDate(dates?: DateInfo[], current?: string): string | undefined {
  if (!dates || dates.length === 0) return undefined;
  if (current && dates.some((d) => d.date === current)) {
    return current;
  }
  return dates[0]?.date;
}

export function ArchiveBrowser() {
  const {
    data: accountsData,
    isLoading: accountsLoading,
    isFetching: accountsFetching,
    error: accountsError
  } = useAccounts();
  const [selectedAccount, setSelectedAccount] = useState<string>("");
  const [selectedYear, setSelectedYear] = useState<string>("");
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(25);
  const [emailsPage, setEmailsPage] = useState<number>(0);
  const [emailsRowsPerPage, setEmailsRowsPerPage] = useState<number>(100);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTarget, setPreviewTarget] = useState<EmailInfo | null>(null);

  const accounts = accountsData?.accounts ?? [];
  const activeAccount = useMemo(
    () => accounts.find((acc) => acc.name === selectedAccount),
    [accounts, selectedAccount]
  );
  const activeAccountLabel = activeAccount?.label ?? activeAccount?.name ?? "";

  useEffect(() => {
    if (!accounts.length) return;
    setSelectedAccount((prev) => (prev && accounts.some((acc) => acc.name === prev) ? prev : getDefaultAccount(accounts) ?? ""));
  }, [accounts]);

  useEffect(() => {
    if (!activeAccount) {
      setSelectedYear("");
      return;
    }
    const next = getDefaultYear(activeAccount, selectedYear);
    setSelectedYear(next ?? "");
  }, [activeAccount, selectedYear]);

  const {
    data: datesData,
    isLoading: datesLoading,
    isFetching: datesFetching
  } = useAccountDates(
    selectedAccount || undefined,
    selectedYear || undefined,
    rowsPerPage,
    page * rowsPerPage
  );

  const dates = datesData?.dates ?? [];
  const totalDates = datesData?.total ?? 0;

  useEffect(() => {
    if (!dates.length) {
      setSelectedDate("");
      return;
    }
    const next = getDefaultDate(dates, selectedDate);
    setSelectedDate(next ?? "");
  }, [dates, selectedDate]);

  useEffect(() => {
    setPage(0);
  }, [selectedAccount, selectedYear]);

  useEffect(() => {
    setEmailsPage(0);
  }, [selectedAccount, selectedYear, selectedDate]);

  useEffect(() => {
    if (page === 0) return;
    if (totalDates === 0) {
      setPage(0);
      return;
    }
    if (page * rowsPerPage >= totalDates) {
      const nextPage = Math.max(Math.ceil(totalDates / rowsPerPage) - 1, 0);
      if (nextPage !== page) {
        setPage(nextPage);
      }
    }
  }, [page, rowsPerPage, totalDates]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (_event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(25);
    setPage(0);
  };

  const handleEmailsChangePage = (_: unknown, newPage: number) => {
    setEmailsPage(newPage);
  };

  const handleEmailsChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setEmailsRowsPerPage(parseInt(event.target.value, 10));
    setEmailsPage(0);
  };

  const {
    data: emailsData,
    isLoading: emailsLoading,
    isFetching: emailsFetching,
    error: emailsError
  } = useEmails(
    selectedAccount || undefined,
    selectedDate || undefined,
    undefined,
    emailsRowsPerPage,
    emailsPage * emailsRowsPerPage
  );

  const emails = emailsData?.emails ?? [];
  const totalEmails = emailsData?.total ?? 0;
  const notify = useNotificationStore((state) => state.enqueue);
  const previewQuery = useEmailPreview(
    selectedAccount || undefined,
    selectedDate || undefined,
    previewTarget?.folder,
    previewTarget?.filename,
    previewOpen
  );

  const downloadWithAuth = async (url: string, suggestedName?: string) => {
    try {
      let requestUrl = url;
      if (url.startsWith(API_BASE_URL)) {
        requestUrl = url.substring(API_BASE_URL.length);
        if (!requestUrl.startsWith("/")) {
          requestUrl = "/" + requestUrl;
        }
      }
      const response = await apiClient.get(requestUrl, { responseType: "blob" });
      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = suggestedName ?? "";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      let message = extractApiError(err);
      const responseData: unknown = (err as any)?.response?.data;
      if (responseData instanceof Blob) {
        try {
          const text = await responseData.text();
          const parsed = JSON.parse(text);
          message = parsed?.detail?.message || parsed?.detail || text || message;
        } catch {
          message = message || "Errore sconosciuto";
        }
      }
      notify({ message: `Download fallito: ${message || "Errore sconosciuto"}`, severity: "error" });
    }
  };

  const handlePreview = (email: EmailInfo) => {
    setPreviewTarget(email);
    setPreviewOpen(true);
  };

  return (
    <>
    <Stack spacing={3} sx={{ width: "100%" }}>
      <Card>
        <CardContent>
          {accountsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : accountsError ? (
            <Alert severity="error">Impossibile caricare gli account configurati.</Alert>
          ) : accounts.length === 0 ? (
            <Alert severity="info">Nessun account configurato nell'archivio.</Alert>
          ) : (
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <FormControl fullWidth disabled={accountsFetching}>
                <InputLabel id="account-select-label">Account</InputLabel>
                <Select
                  labelId="account-select-label"
                  label="Account"
                  value={selectedAccount}
                  onChange={(event: SelectChangeEvent) => setSelectedAccount(event.target.value)}
                >
                  {accounts.map((account) => (
                    <MenuItem key={account.name} value={account.name}>
                      {account.label ?? account.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl
                fullWidth
                disabled={!activeAccount || activeAccount.years.length === 0}
              >
                <InputLabel id="year-select-label">Anno</InputLabel>
                <Select
                  labelId="year-select-label"
                  label="Anno"
                  value={selectedYear}
                  onChange={(event: SelectChangeEvent) => setSelectedYear(event.target.value)}
                >
                  {(activeAccount?.years ?? []).map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          )}
        </CardContent>
      </Card>

      <Grid container spacing={2} columns={{ xs: 1, md: 12 }}>
        <Grid size={{ xs: 1, md: 4 }}>
          <Card sx={{ height: "100%" }}>
            <CardHeader
              title="Date archiviate"
              subheader={selectedAccount ? `${activeAccountLabel} - ${selectedYear || "-"} - ${totalDates} date (pagina ${page + 1}/${Math.max(1, Math.ceil((totalDates || 1) / rowsPerPage))})` : ""}
              action={datesFetching ? <RefreshRoundedIcon className="animate-spin" /> : null}
            />
            <CardContent sx={{ pt: 0, display: "flex", flexDirection: "column", height: "100%" }}>
              {datesLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : dates.length === 0 ? (
                <Alert severity="info">Nessuna data disponibile per questa selezione.</Alert>
              ) : (
                <>
                  <Box sx={{ overflowX: "auto", maxHeight: 520, overflowY: "auto" }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Data</TableCell>
                          <TableCell align="right">Messaggi</TableCell>
                          <TableCell align="right">Archivio compresso</TableCell>
                          <TableCell>Digest SHA-256</TableCell>
                          <TableCell align="right">Azioni</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {dates.map((entry: DateInfo) => {
                          const archiveDisabled = !entry.archive_filename;
                          const digestDisabled = !entry.digest_filename;
                          const archiveSize = formatBytes(entry.archive_size_bytes ?? 0);
                          const digestLabel = formatDigest(entry.archive_sha256);

                          return (
                            <TableRow
                              key={entry.date}
                              hover
                              selected={entry.date === selectedDate}
                              onClick={() => setSelectedDate(entry.date)}
                              sx={{ cursor: "pointer" }}
                            >
                              <TableCell sx={{ minWidth: 180 }}>
                                <Stack spacing={0.5}>
                                  <Stack direction="row" spacing={1} alignItems="center">
                                    <Typography fontWeight={600}>{entry.date}</Typography>
                                    {entry.status ? (
                                      <Chip
                                        size="small"
                                        label={entry.status}
                                        color={resolveStatusColor(entry.status)}
                                        variant="outlined"
                                      />
                                    ) : null}
                                  </Stack>
                                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                    {entry.total_size_bytes
                                      ? `Totale ${formatBytes(entry.total_size_bytes)}`
                                      : " "}
                                  </Typography>
                                  {entry.error_count ? (
                                    <Typography variant="caption" sx={{ color: "error.main" }}>
                                      {entry.error_count} errori
                                    </Typography>
                                  ) : null}
                                </Stack>
                              </TableCell>
                              <TableCell sx={{ minWidth: 180 }}>
                                {entry.archive_sha256 ? (
                                  <Tooltip title={entry.archive_sha256} placement="top">
                                    <Typography component="span" sx={{ fontFamily: "monospace" }}>
                                      {digestLabel}
                                    </Typography>
                                  </Tooltip>
                                ) : entry.digest_filename ? (
                                  <Typography sx={{ fontFamily: "monospace" }}>
                                    {entry.digest_filename}
                                  </Typography>
                                ) : (
                                  <Typography sx={{ color: "text.secondary" }}>-</Typography>
                                )}
                              </TableCell>
                              <TableCell align="right">
                                <Stack direction="row" spacing={1} justifyContent="flex-end">
                                  {selectedAccount ? (
                                    archiveDisabled ? (
                                      <Button size="small" variant="contained" disabled>
                                        Scarica archivio
                                      </Button>
                                    ) : (
                                      <Button
                                        size="small"
                                        variant="contained"
                                        startIcon={<DownloadRoundedIcon />}
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          void downloadWithAuth(
                                            buildArchiveDownloadUrl(selectedAccount, entry.date),
                                            `${selectedAccount}-${entry.date}.tar.gz`
                                          );
                                        }}
                                      >
                                        Scarica archivio
                                      </Button>
                                    )
                                  ) : null}
                                  {selectedAccount ? (
                                    digestDisabled ? (
                                      <Tooltip title="Digest non disponibile">
                                        <span>
                                          <IconButton size="small" disabled>
                                            <FingerprintRoundedIcon fontSize="small" />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                    ) : (
                                      <Tooltip title="Scarica digest (.sha256)">
                                        <IconButton
                                          size="small"
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            void downloadWithAuth(
                                              buildDigestDownloadUrl(selectedAccount, entry.date),
                                              `${selectedAccount}-${entry.date}.sha256`
                                            );
                                          }}
                                        >
                                          <FingerprintRoundedIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    )
                                  ) : null}
                                </Stack>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </Box>
                  <TablePagination
                    component="div"
                    sx={{ mt: 1 }}
                    count={totalDates}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[25]}
                    labelRowsPerPage="Righe per pagina"
                  />
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 1, md: 8 }}>
          <Card sx={{ height: "100%" }}>
            <CardHeader
              title="Email"
              subheader={selectedAccount ? `${activeAccountLabel} - ${selectedDate || "-"} - ${totalEmails} email` : ""}
              action={emailsFetching ? <RefreshRoundedIcon className="animate-spin" /> : null}
            />
            <CardContent sx={{ pt: 0 }}>
              {emailsLoading && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress size={24} />
                </Box>
              )}

              {!emailsLoading && emailsError && (
                <Alert severity="error">
                  Errore durante il caricamento delle email: {extractApiError(emailsError)}
                </Alert>
              )}

              {!emailsLoading && !emailsError && emails.length === 0 && (
                <Alert severity="info">Nessuna email trovata per questa data.</Alert>
              )}

              {!emailsLoading && !emailsError && emails.length > 0 && selectedAccount && selectedDate && (
                <Box sx={{ overflowX: "auto" }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Oggetto</TableCell>
                        <TableCell>Mittente</TableCell>
                        <TableCell>Destinatari</TableCell>
                        <TableCell>Cartella</TableCell>
                        <TableCell>Data</TableCell>
                        <TableCell align="right">Dimensione</TableCell>
                        <TableCell align="right">Azioni</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {emails.map((email) => (
                        <TableRow key={`${email.uid}-${email.folder}`} hover>
                          <TableCell sx={{ minWidth: 200 }}>{email.subject || "(Senza oggetto)"}</TableCell>
                          <TableCell sx={{ minWidth: 160 }}>{email.sender}</TableCell>
                          <TableCell sx={{ minWidth: 200 }}>{email.to}</TableCell>
                          <TableCell>{email.folder}</TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {new Date(email.date).toLocaleString()}
                          </TableCell>
                          <TableCell align="right">{formatBytes(email.size)}</TableCell>
                          <TableCell align="right">
                            <ButtonGroup size="small" variant="outlined">
                              <Button onClick={() => handlePreview(email)}>Anteprima</Button>
                              <Button
                                startIcon={<DownloadRoundedIcon />}
                                onClick={() =>
                                  downloadWithAuth(
                                    buildEmailDownloadUrl(selectedAccount, selectedDate, email),
                                    email.filename
                                  )
                                }
                              >
                                Scarica
                              </Button>
                            </ButtonGroup>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <TablePagination
                    component="div"
                    count={totalEmails}
                    page={emailsPage}
                    onPageChange={handleEmailsChangePage}
                    rowsPerPage={emailsRowsPerPage}
                    onRowsPerPageChange={handleEmailsChangeRowsPerPage}
                    rowsPerPageOptions={[50, 100, 200, 500]}
                    labelRowsPerPage="Email per pagina"
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Stack>

    <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} fullWidth maxWidth="md">
      <DialogTitle>Anteprima email</DialogTitle>
      <DialogContent dividers>
        {previewQuery.isLoading ? (
          <Stack alignItems="center" sx={{ py: 4 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Caricamento anteprima...
            </Typography>
          </Stack>
        ) : previewQuery.isError ? (
          <Alert severity="error">Impossibile caricare l'anteprima.</Alert>
        ) : previewQuery.data ? (
          <Stack spacing={1}>
            <Typography variant="subtitle1" fontWeight={700}>
              {previewQuery.data.subject || "(Senza oggetto)"}
            </Typography>
            <Divider />
            <Typography variant="body2">
              <strong>Da:</strong> {previewQuery.data.sender || "-"}
            </Typography>
            <Typography variant="body2">
              <strong>A:</strong> {previewQuery.data.to || "-"}
            </Typography>
            {previewQuery.data.cc ? (
              <Typography variant="body2">
                <strong>CC:</strong> {previewQuery.data.cc}
              </Typography>
            ) : null}
            <Typography variant="body2">
              <strong>Data:</strong> {previewQuery.data.date || "-"}
            </Typography>
            <Divider />
            {previewQuery.data.html ? (
              <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 1, bgcolor: "background.paper" }}>
                <Typography variant="caption" color="text.secondary">
                  Corpo HTML
                </Typography>
                <Box sx={{ mt: 1, maxHeight: 400, height: 400, bgcolor: "#fff" }}>
                  <iframe
                    title="preview-html"
                    srcDoc={`<!doctype html><html><head><meta charset="utf-8"/><style>body{margin:0;padding:12px;font-family:Arial,sans-serif;background:#fff;color:#111;} a{color:#1976d2;}</style></head><body>${previewQuery.data.html}</body></html>`}
                    style={{ border: "none", width: "100%", height: "100%" }}
                    sandbox="allow-same-origin"
                  />
                </Box>
              </Box>
            ) : null}
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
              {previewQuery.data.body || "(Corpo non disponibile)"}
            </Typography>
            {previewQuery.data.attachments && previewQuery.data.attachments.length > 0 ? (
              <Stack spacing={1} sx={{ mt: 2 }}>
                <Typography variant="subtitle2">Allegati</Typography>
                {previewQuery.data.attachments.map((att) => (
                  <Stack key={att.id} direction="row" spacing={1} alignItems="center">
                            <Typography variant="body2" sx={{ flexGrow: 1 }}>
                              {att.filename} {att.size ? `(${(att.size / 1024).toFixed(1)} KB)` : ""}
                            </Typography>
                            {selectedAccount && selectedDate && previewTarget ? (
                              <Button
                                size="small"
                                onClick={() =>
                                  downloadWithAuth(
                                    buildAttachmentDownloadUrl(
                                      selectedAccount,
                                      selectedDate,
                                      previewTarget.folder,
                                      previewTarget.filename,
                                      att.id
                                    ),
                                    att.filename
                                  )
                                }
                              >
                                Scarica
                              </Button>
                            ) : null}
                          </Stack>
                        ))}
                      </Stack>
                    ) : null}
          </Stack>
        ) : (
          <Alert severity="info">Seleziona una email per vedere l'anteprima.</Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setPreviewOpen(false)}>Chiudi</Button>
      </DialogActions>
    </Dialog>
    </>
  );
}




